import { useMemo, type FC } from "react";
import type { CarReservationComponentProps } from "@apac/cars";
import { CORE_CARS_NAME } from "@apac/components/cars";
import { useSteppedCancellationRule } from "@b2bportal/core-cars";
import { IconNameEnum, useModuleBEM, useStyles } from "@b2bportal/core-themes";
import { ensureExhaustive } from "@b2bportal/core-utilities";
import type { Reservation } from "@b2bportal/ground-api";
import { RestrictionCancelEnum } from "@b2bportal/ground-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
  ButtonTypeEnum,
  ExpandableVendorMessage,
  Icon,
} from "@tenant/ui";
import defaultStyles from "./CarReservationPolicies.module.scss";

export interface CarReservationPoliciesProps
  extends CarReservationComponentProps {
  reservation: Reservation;
  onCancel?: () => void;
}

const ComponentName = "CarReservationPolicies";

export const CarReservationPolicies: FC<CarReservationPoliciesProps> = ({
  reservation,
  onCancel,
}) => {
  const styles = useStyles(CORE_CARS_NAME, ComponentName, defaultStyles);
  const [block, cn] = useModuleBEM(styles, ComponentName);
  const { t } = useI18nContext();

  const { cancellationRules } = reservation;

  const { copy: steppedCancellationCopyArray } =
    useSteppedCancellationRule(cancellationRules);

  // Determine if the booking is cancellable
  const isCancellable =
    cancellationRules?.RestrictionCancel === RestrictionCancelEnum.Free ||
    cancellationRules?.RestrictionCancel ===
      RestrictionCancelEnum.Conditional ||
    cancellationRules?.RestrictionCancel ===
      RestrictionCancelEnum.ConditionalAfterBooking;

  // Get cancellation policy text
  const cancellationPolicyText = useMemo(() => {
    const restrictionCancel = cancellationRules?.RestrictionCancel;

    switch (restrictionCancel) {
      case RestrictionCancelEnum.Free:
        return t("core-cars.cancellation.free");
      case RestrictionCancelEnum.Conditional:
      case RestrictionCancelEnum.ConditionalAfterBooking:
        return cancellationRules?.description || "";
      case RestrictionCancelEnum.NonRefundable:
      case RestrictionCancelEnum.Paid:
        return t("core-cars.cancellation.nonRefundable");
      case RestrictionCancelEnum.SteppedCancellationRule:
        return t("core-cars.cancellation.steppedCancellation");
      case undefined:
        return "";
      default:
        return ensureExhaustive(restrictionCancel, "");
    }
  }, [cancellationRules, t]);

  return (
    <div className={block}>
      <h3 className={cn("title")}>{t("trips.policies")}</h3>

      {/* Cancellation Policy Section */}
      {cancellationPolicyText && (
        <div className={cn("cancellation-policy-section")}>
          <div className={cn("policy-header")}>
            <Icon
              iconName={IconNameEnum.cfar__shieldInfo}
              className={cn("policy-icon")}
            />
            <p className={cn("policy-title")}>{cancellationPolicyText}</p>
          </div>
          {steppedCancellationCopyArray.length > 0 && (
            <div>
              {steppedCancellationCopyArray.map((copy, i) => (
                <p
                  className={cn("policy-text")}
                  key={`stepped-cancellation-copy-${i}`}
                >
                  {copy}
                </p>
              ))}
            </div>
          )}

          {isCancellable && onCancel && (
            <Button
              buttonType={ButtonTypeEnum.Secondary}
              className={cn("cancel-button")}
              onClick={onCancel}
            >
              {t("core-cars.postbooking.cancelBooking")}
            </Button>
          )}
        </div>
      )}

      {/* Important Information Accordion */}
      {reservation?.vendorMessages?.importantInfo && (
        <Accordion type="multiple">
          <AccordionItem value={"title"}>
            <AccordionTrigger>
              <div className={cn("policy-title")}>
                {reservation.vendorMessages.importantInfo.desc}
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <ExpandableVendorMessage
                initialAmount={3}
                className={cn("policy-content")}
                vendorMessages={
                  reservation.vendorMessages.importantInfo.details
                }
              />
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      )}
    </div>
  );
};
